"use client";

import { useCallback, useEffect, useState } from "react";
import { MessageType } from "./types";
import { useBridge } from "./useBridge";

type DownloadTaskStatus =
  | "queued"
  | "started"
  | "progress"
  | "completed"
  | "failed";

export interface DownloadTaskState {
  taskId: string;
  fileName: string;
  status: DownloadTaskStatus;
  progress: number;
  errorMessage?: string;
}

export interface StartDownloadParams {
  url: string;
  fileName: string;
  headers?: Record<string, string>;
  mimeType?: string;
  /** 是否在 iOS 下载完成后弹出系统保存/分享面板。默认 true。 */
  showSavePanel?: boolean;
}

export function createDownloadTaskId(): string {
  return `download_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function normalizeDownloadProgress(progress?: number): number {
  if (typeof progress !== "number" || Number.isNaN(progress)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(progress)));
}

export function useFileDownloadBridge() {
  const { isInApp, callMethod, on } = useBridge();
  const [tasks, setTasks] = useState<Record<string, DownloadTaskState>>({});

  const updateTask = useCallback(
    (taskId: string, patch: Partial<DownloadTaskState>) => {
      setTasks((prev) => {
        const current = prev[taskId];
        if (!current) {
          return prev;
        }
        return {
          ...prev,
          [taskId]: {
            ...current,
            ...patch,
          },
        };
      });
    },
    [],
  );

  useEffect(() => {
    if (!isInApp) {
      return;
    }
    const offStarted = on(MessageType.DOWNLOAD_STARTED, (data: any) => {
      const taskId = String(data?.taskId || "");
      if (!taskId) {
        return;
      }
      updateTask(taskId, { status: "started" });
    });
    const offProgress = on(MessageType.DOWNLOAD_PROGRESS, (data: any) => {
      const taskId = String(data?.taskId || "");
      if (!taskId) {
        return;
      }
      updateTask(taskId, {
        status: "progress",
        progress: normalizeDownloadProgress(data?.progress),
      });
    });
    const offCompleted = on(MessageType.DOWNLOAD_COMPLETED, (data: any) => {
      const taskId = String(data?.taskId || "");
      if (!taskId) {
        return;
      }
      updateTask(taskId, { status: "completed", progress: 100 });
    });
    const offFailed = on(MessageType.DOWNLOAD_FAILED, (data: any) => {
      const taskId = String(data?.taskId || "");
      if (!taskId) {
        return;
      }
      updateTask(taskId, {
        status: "failed",
        errorMessage:
          typeof data?.message === "string" && data.message
            ? data.message
            : undefined,
      });
    });
    return () => {
      offStarted();
      offProgress();
      offCompleted();
      offFailed();
    };
  }, [isInApp, on, updateTask]);

  const startDownload = useCallback(
    async (params: StartDownloadParams): Promise<{ taskId: string }> => {
      const taskId = createDownloadTaskId();
      setTasks((prev) => ({
        ...prev,
        [taskId]: {
          taskId,
          fileName: params.fileName,
          status: "queued",
          progress: 0,
        },
      }));
      await callMethod("downloadFile", {
        ...params,
        taskId,
      });
      return { taskId };
    },
    [callMethod],
  );

  const getTask = useCallback(
    (taskId?: string | null): DownloadTaskState | undefined => {
      if (!taskId) {
        return undefined;
      }
      return tasks[taskId];
    },
    [tasks],
  );

  return {
    isInApp,
    startDownload,
    getTask,
    tasks,
  };
}
